/**
 * PushPullTool — SketchUp gestures on extrude cap faces, wired parametrically.
 *
 * Plain drag: edits the extrude's `distance` (the face tracks the cursor 1:1
 * along the extrude axis). Alt/Option-drag on the PUSHED cap: edits
 * `endScale` — dragging radially away from the profile centroid flares the
 * end, toward it tapers (down to 0 = cone/pyramid apex).
 *
 * Both run through the document PREVIEW channel (the evaluator cache keeps
 * each pass to a single kernel execution) and commit ONE undo step on
 * release, with a live readout in the dimension box.
 *
 * Extrude-WALL and non-extrude B-rep presses on a PLANAR face start a
 * press-pull feature drag (prism the face, fuse/cut). Empty space, curved
 * faces, and mesh-only bodies return null so the camera keeps its orbit.
 */

import * as THREE from 'three/webgpu';

import type { CadDocument } from '@core/document';
import { centroidOf } from '@core/geometry/extrude';
import { composeAxisScale } from '@core/geometry/meshUtils';
import { planeBasis, uvToWorld } from '@core/geometry/sketch';
import type {
  ExtrudeParams,
  FeatureNode,
  ScaleParams,
  SketchParams,
} from '@core/timeline/types';
import type { Timeline } from '@core/timeline/timeline';
import type { DragHandler } from '@render/cadControls';
import type { Viewport } from '@render/viewport';
import type { DimInput } from '@ui/dimInput';

const CAP_ALIGN = 0.95; // |dot(face normal, axis)| above this = cap face

export class PushPullTool {
  constructor(
    private readonly viewport: Viewport,
    /** Live document access — the host may swap projects under us. */
    private readonly host: { readonly doc: CadDocument },
    private readonly dim?: DimInput,
  ) {}

  /** CADControls.onDragStart hook. */
  tryStart = (e: PointerEvent): DragHandler | null => {
    const hit = this.viewport.raycastBodies(e.clientX, e.clientY);
    if (!hit) return null;

    const feature = this.host.doc.timeline.get(hit.featureId);
    if (!feature) return null;
    // Dragging a Scale feature's body resizes it about its stored center.
    if (feature.type === 'scale') {
      return this.startBodyScale(feature as FeatureNode<'scale'>, e);
    }
    if (feature.type !== 'extrude') {
      // Not an extrude: direct-model ANY planar face of a B-rep body by
      // adding a press-pull feature (prism the face, fuse/cut).
      return this.startPressPull(feature.id, hit, e);
    }
    const extrude = feature as FeatureNode<'extrude'>;
    const params = extrude.params as ExtrudeParams;

    const sketch = this.host.doc.timeline.get(params.profile.$feature);
    if (!sketch || sketch.type !== 'sketch') return null;
    // Axis = the sketch plane normal. Prefer the EVALUATED plane (face-
    // attached sketches resolve at eval time); fall back to datum math.
    let axis: THREE.Vector3;
    const skResult = this.host.doc.lastReport?.results.get(sketch.id);
    if (skResult?.kind === 'sketch') {
      const m = skResult.planeWorld;
      axis = new THREE.Vector3(m[8]!, m[9]!, m[10]!);
    } else {
      try {
        axis = new THREE.Vector3(...planeBasis((sketch.params as SketchParams).plane).normal);
      } catch {
        return null;
      }
    }

    const alignment = hit.normal.dot(axis);
    if (Math.abs(alignment) < CAP_ALIGN) {
      // Extrude WALL: not the parametric cap — press-pull the face instead.
      return this.startPressPull(extrude.id, hit, e);
    }
    const sign = alignment > 0 ? 1 : -1;

    return e.altKey
      ? this.startTaper(extrude, hit.point, axis, sign, e)
      : this.startDistance(extrude, hit.point, axis, sign, e);
  };

  // --- drag on any planar face: press-pull feature -----------------------------

  private startPressPull(
    targetId: string,
    hit: {
      point: THREE.Vector3;
      normal: THREE.Vector3;
      face?: { kind: string; dir: [number, number, number] };
    },
    e: PointerEvent,
  ): DragHandler | null {
    // Only exact B-rep bodies can be press-pulled (the kernel prisms a face),
    // and only PLANAR faces — a drag on a cylinder wall falls through to the
    // camera rather than prisming some unrelated nearest planar face.
    const result = this.host.doc.lastReport?.results.get(targetId as never);
    if (result?.kind !== 'body' || !result.brepId) return null;
    if (hit.face?.kind !== 'planar') return null;
    // Drag along the EXACT plane normal (the tri normal can be off slightly).
    const axis = new THREE.Vector3(...hit.face.dir).normalize();
    const facePoint: [number, number, number] = [hit.point.x, hit.point.y, hit.point.z];
    const base = this.host.doc.timeline;
    const name = `Press/Pull ${base.features.filter((f) => f.type === 'press-pull').length + 1}`;
    // One pending feature whose offset the drag edits (single id → the
    // evaluator cache sees one feature changing, one kernel op per move).
    const added = base.addFeature('press-pull', name, {
      target: { $feature: targetId as never },
      facePoint,
      offset: 0.5,
    });
    const pendingTimeline = added.timeline;
    const fid = added.feature.id;
    const startT = this.axisParam(axis, hit.point, e);
    let lastSnapped: number | null = null;
    let lastPreview: Promise<import('@core/timeline/evaluator').EvaluationReport> | null = null;

    return {
      move: (ev: PointerEvent): void => {
        const delta = this.axisParam(axis, hit.point, ev) - startT;
        const snapped = Math.round(delta * 2) / 2;
        if (Math.abs(snapped) < 0.5 || snapped === lastSnapped) return;
        lastSnapped = snapped;
        this.dim?.show(
          `${snapped > 0 ? '+' : ''}${snapped} mm`,
          ev.clientX,
          ev.clientY,
          null,
        );
        lastPreview = this.host.doc.preview(
          pendingTimeline.updateFeature(fid, { target: { $feature: targetId as never }, facePoint, offset: snapped }),
        );
      },
      up: (): void => {
        this.dim?.hide();
        // A click (no preview ever issued) must not cancelPreview() — that
        // would discard someone ELSE's in-flight preview.
        if (lastSnapped === null || !lastPreview) return;
        const finalOffset = lastSnapped;
        void lastPreview.then(async (report) => {
          const ok = report.statuses.get(fid)?.state === 'ok';
          // Replay onto the CURRENT head instead of committing the drag-start
          // timeline: a concurrent doc.apply (AI copilot / MCP) mid-drag
          // would otherwise be silently clobbered. Broken previews (offset
          // consumed the body) are dropped rather than committed.
          await this.host.doc.cancelPreview();
          if (!ok) return;
          await this.host.doc.apply(name, (t) =>
            t.addFeature('press-pull', name, {
              target: { $feature: targetId as never },
              facePoint,
              offset: finalOffset,
            }).timeline,
          );
        });
      },
    };
  }

  // --- plain drag: distance ---------------------------------------------------

  private startDistance(
    extrude: FeatureNode<'extrude'>,
    hitPoint: THREE.Vector3,
    axis: THREE.Vector3,
    sign: number,
    _e: PointerEvent,
  ): DragHandler {
    const params = extrude.params as ExtrudeParams;
    const base = this.host.doc.timeline;
    const startDistance = params.distance;
    const startT = this.axisParam(axis, hitPoint, _e);
    let lastPreview: Promise<unknown> = Promise.resolve();

    return {
      move: (ev: PointerEvent): void => {
        const delta = (this.axisParam(axis, hitPoint, ev) - startT) * sign;
        const snapped = Math.round((startDistance + delta) * 2) / 2;
        if (Math.abs(snapped) < 0.5 || snapped === this.currentParams(extrude).distance) return;
        this.dim?.show(`${snapped} mm`, ev.clientX, ev.clientY, null);
        lastPreview = this.host.doc.preview(
          base.updateFeature(extrude.id, { ...params, distance: snapped }),
        );
      },
      up: (): void => {
        this.dim?.hide();
        void lastPreview.then(() => this.host.doc.commitPreview(`Push/Pull ${extrude.name}`));
      },
    };
  }

  // --- alt-drag: taper (endScale) ------------------------------------------------

  private startTaper(
    extrude: FeatureNode<'extrude'>,
    hitPoint: THREE.Vector3,
    axis: THREE.Vector3,
    sign: number,
    e: PointerEvent,
  ): DragHandler | null {
    const params = extrude.params as ExtrudeParams;
    // Taper edits the PUSHED cap only; the sketch-side cap stays as drawn.
    const farSign = params.distance >= 0 ? 1 : -1;
    if (sign !== farSign) return null;

    // Scale center: the profile centroid lifted to the cap plane — matches
    // exactly what the kernel scales about.
    const skResult = this.host.doc.lastReport?.results.get(params.profile.$feature);
    if (skResult?.kind !== 'sketch') return null;
    const regionIdx = (() => {
      const m = /^profile:(\d+)$/.exec(params.profile.path ?? '');
      return m ? Number(m[1]) : 0;
    })();
    const region = skResult.profiles[regionIdx] ?? skResult.profiles[0];
    if (!region) return null;
    const c2 = centroidOf(region.outer);
    const center = new THREE.Vector3(...uvToWorld(skResult.planeWorld, c2)).addScaledVector(
      axis,
      params.distance,
    );
    const capPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(axis, center);

    const startR = hitPoint.distanceTo(center);
    if (startR < 1e-3) return null; // grabbed the exact center — ambiguous
    const startScale = Math.max(params.endScale ?? 1, 1e-6);
    const base = this.host.doc.timeline;
    let lastPreview: Promise<unknown> = Promise.resolve();
    this.dim?.show(`× ${startScale.toFixed(2)}`, e.clientX, e.clientY, null);

    return {
      move: (ev: PointerEvent): void => {
        const p = this.pointOnPlane(capPlane, ev);
        if (!p) return;
        const raw = (startScale * p.distanceTo(center)) / startR;
        const snapped = Math.min(50, Math.max(0, Math.round(raw * 20) / 20));
        this.dim?.show(
          snapped < 0.05 ? '× 0 (apex)' : `× ${snapped.toFixed(2)}`,
          ev.clientX,
          ev.clientY,
          null,
        );
        if (snapped === (this.currentParams(extrude).endScale ?? 1)) return;
        lastPreview = this.host.doc.preview(
          base.updateFeature(extrude.id, { ...params, endScale: snapped }),
        );
      },
      up: (): void => {
        this.dim?.hide();
        void lastPreview.then(() => this.host.doc.commitPreview(`Taper ${extrude.name}`));
      },
    };
  }

  // --- drag on a Scale feature: factor ------------------------------------------

  private startBodyScale(
    scale: FeatureNode<'scale'>,
    e: PointerEvent,
  ): DragHandler | null {
    const params = scale.params as ScaleParams;
    const uniform = params.factor ?? 1;
    const baseFactors: [number, number, number] = [
      ...(params.factors ?? [uniform, uniform, uniform]),
    ] as [number, number, number];
    const baseCenter: [number, number, number] = [...params.center] as [number, number, number];

    // Uniform drag scales about the body's CURRENT bbox center.
    const result = this.host.doc.lastReport?.results.get(scale.id);
    if (result?.kind !== 'body' || result.mesh.positions.length === 0) return null;
    const p = result.mesh.positions;
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < p.length; i += 3) {
      for (let k = 0; k < 3; k++) {
        const v = p[i + k]!;
        if (v < min[k]!) min[k] = v;
        if (v > max[k]!) max[k] = v;
      }
    }
    const center = new THREE.Vector3(
      (min[0]! + max[0]!) / 2,
      (min[1]! + max[1]!) / 2,
      (min[2]! + max[2]!) / 2,
    );
    const viewDir = this.viewport.camera.getWorldDirection(new THREE.Vector3());
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(viewDir, center);
    const p0 = this.pointOnPlane(plane, e);
    if (!p0) return null;
    const startR = p0.distanceTo(center);
    if (startR < 1e-3) return null;

    const base = this.host.doc.timeline;
    let lastPreview: Promise<unknown> = Promise.resolve();

    return {
      move: (ev: PointerEvent): void => {
        const pt = this.pointOnPlane(plane, ev);
        if (!pt) return;
        const s = Math.max(0.02, Math.round((pt.distanceTo(center) / startR) * 40) / 40);
        let cur: { center: [number, number, number]; factors: [number, number, number] } = {
          center: baseCenter,
          factors: baseFactors,
        };
        for (const axis of [0, 1, 2] as const) {
          if (cur.factors[axis] * s < 0.01) continue;
          cur = composeAxisScale(cur.center, cur.factors, axis, s, center.getComponent(axis));
        }
        this.dim?.show(
          `× ${(cur.factors[0] / baseFactors[0]).toFixed(2)}`,
          ev.clientX,
          ev.clientY,
          null,
        );
        lastPreview = this.host.doc.preview(
          base.updateFeature(scale.id, { ...params, center: cur.center, factors: cur.factors }),
        );
      },
      up: (): void => {
        this.dim?.hide();
        void lastPreview.then(() => this.host.doc.commitPreview(`Scale ${scale.name}`));
      },
    };
  }

  // --- shared math ------------------------------------------------------------

  private currentParams(extrude: FeatureNode<'extrude'>): ExtrudeParams {
    return (this.host.doc.timeline.get(extrude.id)?.params ?? extrude.params) as ExtrudeParams;
  }

  private pointerRay(e: PointerEvent): THREE.Raycaster {
    const rect = this.viewport.element.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, this.viewport.camera);
    return ray;
  }

  private pointOnPlane(plane: THREE.Plane, e: PointerEvent): THREE.Vector3 | null {
    return this.pointerRay(e).ray.intersectPlane(plane, new THREE.Vector3());
  }

  /** Parameter (mm) of the pointer ray's closest point along the drag axis. */
  private axisParam(axis: THREE.Vector3, origin: THREE.Vector3, e: PointerEvent): number {
    const ray = this.pointerRay(e);
    // Closest point on the axis line to the pointer ray, via a long segment.
    const a = origin.clone().addScaledVector(axis, -1e5);
    const b = origin.clone().addScaledVector(axis, 1e5);
    const onSegment = new THREE.Vector3();
    ray.ray.distanceSqToSegment(a, b, undefined, onSegment);
    return onSegment.sub(origin).dot(axis);
  }
}
